/**
 * Booking Confirmation Modal
 * Shows booking details for confirmation before creating booking
 * Validates addresses and allows editing of booking details
 */

import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  DatePicker,
  Select,
  Button,
  Space,
  message,
  Card,
  Typography,
  Alert,
  Divider,
  Row,
  Col,
  Tag,
  Checkbox,
} from "antd";
import {
  CheckCircleOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  HomeOutlined,
  WarningOutlined,
  EditOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { createBooking } from "../services/bookingService";
import { createDriver } from "../../drivers/services/driverService";
import { getPartners } from "../../partners/services/partnerService";
import PartnerSearchAutocomplete from "../../../shared/components/PartnerSearchAutocomplete";
import ProviderDriverAssignmentFields from "../../drivers/components/ProviderDriverAssignmentFields";
import {
  isBubakiiProvider,
} from "../../drivers/utils/providerDriverAssignment";
import ResponsiveDrawer from "../../../shared/components/ResponsiveDrawer.jsx";
import { AddressValidationService } from "../../../shared/services/addressValidationService";
import { buildBookingNotesFromLead } from "../../../shared/utils/leadAccessibility";
import { getOrder } from "../../orders/services/orderService";
import { getLead } from "../../leads/services/leadService";
import UnsafeAreaAlert from "../../../shared/components/UnsafeAreaAlert.jsx";
import { requiresUnsafeAreaAcknowledgment } from "../../../shared/utils/unsafeAreaAlert.js";

const { TextArea } = Input;
const { Text, Title } = Typography;
const { Option } = Select;

const BookingConfirmationModal = ({
  visible,
  quote,
  orderId,
  onClose,
  onSuccess,
  userId,
}) => {
  const [form] = Form.useForm();
  const [createDriverForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [createDriverModalVisible, setCreateDriverModalVisible] = useState(false);
  const [driverReloadToken, setDriverReloadToken] = useState(0);
  const [createdDriver, setCreatedDriver] = useState(null);
  const [newDriverData, setNewDriverData] = useState({
    name: "",
    phone: "",
    email: "",
    licenseNumber: "",
    licenseExpiry: null,
    ownerType: "bubakii",
    partnerId: "",
  });
  const [partners, setPartners] = useState([]);
  const [creatingDriver, setCreatingDriver] = useState(false);
  const [createDriverError, setCreateDriverError] = useState(null);
  const [addressErrors, setAddressErrors] = useState({
    pickup: null,
    delivery: null,
  });
  const [addressValidated, setAddressValidated] = useState({
    pickup: false,
    delivery: false,
  });
  const [editingAddress, setEditingAddress] = useState({
    pickup: false,
    delivery: false,
  });
  const [addressInputs, setAddressInputs] = useState({
    pickup: "",
    delivery: "",
  });
  const [savingAddress, setSavingAddress] = useState({
    pickup: false,
    delivery: false,
  });
  const [manualVehicle, setManualVehicle] = useState("");
  const [vehicleType, setVehicleType] = useState("medium");
  const [unsafeAreaAcknowledged, setUnsafeAreaAcknowledged] = useState(false);

  // Load order data when modal opens
  useEffect(() => {
    if (visible && orderId) {
      loadOrderData();
    }
  }, [visible, orderId]);

  // Validate addresses when order loads
  useEffect(() => {
    if (order) {
      validateAddresses();
      initializeForm();
    }
  }, [order]);

  // Load partners for driver creation
  useEffect(() => {
    const loadPartners = async () => {
      try {
        const result = await getPartners({ isActive: true });
        if (result.success) {
          setPartners(result.data || []);
        }
      } catch (err) {
        console.error("Error loading partners:", err);
      }
    };
    loadPartners();
  }, []);

  // Load order data
  const loadOrderData = async () => {
    setLoadingOrder(true);
    try {
      const orderData = await getOrder(orderId);
      setOrder(orderData);
    } catch (error) {
      // If the order ID doesn't exist, try to create it from the quote
      const isNotFound =
        error?.status === 404 ||
        error?.response?.status === 404 ||
        /order not found/i.test(error?.message || "") ||
        /failed to fetch order/i.test(error?.message || "");

      if (isNotFound && quote?.id) {
        try {
          const { createOrderFromQuote } = await import(
            "../../orders/services/orderService"
          );

          message.loading("Creating order from quote...", 0);
          const createdOrder = await createOrderFromQuote({
            quoteId: quote.id,
            notes: "Auto-created for booking confirmation",
          });

          message.destroy();
          
          // The createOrderFromQuote returns the order data directly
          // Reload the order to get the full order object with all fields
          if (createdOrder?.id) {
            // Use the orderId from the created order to reload full order data
            const fullOrderData = await getOrder(createdOrder.id);
            setOrder(fullOrderData);
            message.success("Order created successfully");
            return;
          } else {
            // Fallback: use the created order directly if it has the structure we need
            setOrder(createdOrder);
            message.success("Order created successfully");
            return;
          }
        } catch (createErr) {
          message.destroy();
          console.error("Error creating order from quote:", createErr);
          message.error(
            createErr.message || "Failed to create order from quote"
          );
        }
      } else {
        console.error("Error loading order:", error);
        message.error("Failed to load order data");
      }
    } finally {
      setLoadingOrder(false);
    }
  };

  // Initialize form with quote and order data
  const initializeForm = async () => {
    if (!quote || !order) return;

    // Validate addresses on load
    validateAddresses();

    // Set initial form values
    const moveDate = quote.dates?.[0]
      ? dayjs(quote.dates[0])
      : dayjs().add(7, "days");

    const defaultNotes = {
      stairs: "",
      accessRules: "",
      fragileItems: "",
      gateCode: "",
      estateName: "",
      contactOnDay: quote.customerPhone || order.customerPhone || "",
    };

    let notes = { ...defaultNotes };

    if (order.booking?.notes) {
      notes = {
        ...notes,
        ...order.booking.notes,
        contactOnDay:
          order.booking.notes.contactOnDay ||
          quote.customerPhone ||
          order.customerPhone ||
          "",
      };
    }

    if (quote.leadId) {
      try {
        const leadResult = await getLead(quote.leadId);
        if (leadResult.success && leadResult.data) {
          notes = buildBookingNotesFromLead(leadResult.data, notes);
        }
      } catch (error) {
        console.error("Error loading lead accessibility for booking notes:", error);
      }
    }

    form.setFieldsValue({
      moveDate: moveDate,
      pickupTimeWindow: "morning",
      driverId: null,
      notes,
    });

    setCreatedDriver(null);
    // BRIDGE: Reset manual vehicle fields
    setManualVehicle("");
    setVehicleType("medium");
  };

  // Validate addresses
  const validateAddresses = () => {
    if (!order?.pickup?.address || !order?.delivery?.address) {
      setAddressErrors({
        pickup: "Pickup address is required",
        delivery: "Delivery address is required",
      });
      return false;
    }

    const pickupValidation = AddressValidationService.validateAddress(
      order.pickup.address
    );
    const deliveryValidation = AddressValidationService.validateAddress(
      order.delivery.address
    );

    const errors = {
      pickup: pickupValidation.isValid ? null : pickupValidation.error,
      delivery: deliveryValidation.isValid ? null : deliveryValidation.error,
    };

    setAddressErrors(errors);
    setAddressValidated({
      pickup: pickupValidation.isValid,
      delivery: deliveryValidation.isValid,
    });

    return pickupValidation.isValid && deliveryValidation.isValid;
  };

  // Handle address editing
  const handleEditAddress = (type) => {
    setEditingAddress((prev) => ({ ...prev, [type]: true }));
    setAddressInputs((prev) => ({
      ...prev,
      [type]: order?.[type]?.address || "",
    }));
  };

  const handleCancelEditAddress = (type) => {
    setEditingAddress((prev) => ({ ...prev, [type]: false }));
    setAddressInputs((prev) => ({ ...prev, [type]: "" }));
  };

  const handleSaveAddress = async (type) => {
    const address = addressInputs[type]?.trim();

    if (!address) {
      message.error(
        `${type === "pickup" ? "Pickup" : "Delivery"} address is required`
      );
      return;
    }

    // Validate address format
    const validation = AddressValidationService.validateAddress(address);
    if (!validation.isValid) {
      message.error(validation.error || "Invalid address format");
      return;
    }

    // Require a real loaded order (don't rely on orderId prop, it may be stale)
    if (!order?.id) {
      message.error(
        "Order not loaded yet. Please wait a moment and try again."
      );
      return;
    }

    setSavingAddress((prev) => ({ ...prev, [type]: true }));

    try {
      const { updateOrder } = await import("../../orders/services/orderService");

      // Use dot notation for nested field updates (more reliable with MongoDB)
      const updateData = {
        [`${type}.address`]: address,
      };

      const result = await updateOrder(order.id, updateData);

      if (result.success) {
        // Reload order to get updated data
        await loadOrderData();
        setEditingAddress((prev) => ({ ...prev, [type]: false }));
        message.success(
          `${type === "pickup" ? "Pickup" : "Delivery"} address updated successfully`
        );
      } else {
        message.error(result.error || "Failed to update address");
      }
    } catch (error) {
      console.error("Error updating address:", error);
      message.error(error.message || "Failed to update address");
    } finally {
      setSavingAddress((prev) => ({ ...prev, [type]: false }));
    }
  };

  // Handle creating new driver
  const handleCreateDriver = async () => {
    if (!newDriverData.name.trim()) {
      setCreateDriverError("Driver name is required");
      return;
    }
    if (!newDriverData.phone.trim()) {
      setCreateDriverError("Phone number is required");
      return;
    }
    if (!newDriverData.licenseNumber.trim()) {
      setCreateDriverError("License number is required");
      return;
    }
    if (!newDriverData.licenseExpiry) {
      setCreateDriverError("License expiry date is required");
      return;
    }
    if (newDriverData.ownerType === "third_party" && !newDriverData.partnerId) {
      setCreateDriverError("Partner is required for 3rd party drivers");
      return;
    }

    setCreatingDriver(true);
    setCreateDriverError(null);

    try {
      const result = await createDriver({
        ...newDriverData,
        licenseExpiry: newDriverData.licenseExpiry.toISOString(),
        partnerId: newDriverData.ownerType === "third_party" ? newDriverData.partnerId : undefined,
      });

      if (result.success) {
        const newDriver = result.data;
        form.setFieldsValue({ driverId: newDriver.id });
        setCreatedDriver(newDriver);
        setDriverReloadToken((token) => token + 1);
        setCreateDriverModalVisible(false);
        setNewDriverData({
          name: "",
          phone: "",
          email: "",
          licenseNumber: "",
          licenseExpiry: null,
          ownerType: "bubakii",
          partnerId: "",
        });
        message.success("Driver created successfully");
      } else {
        setCreateDriverError(result.error || "Failed to create driver");
      }
    } catch (err) {
      setCreateDriverError(err.message || "Failed to create driver");
    } finally {
      setCreatingDriver(false);
    }
  };

  // Handle date change
  const handleDateChange = (date) => {
    form.setFieldsValue({ moveDate: date });
  };

  // Handle form submission
  const handleSubmit = async (values) => {
    // Validate addresses first
    if (!validateAddresses()) {
      message.error("Please fix address validation errors before proceeding");
      return;
    }

    const unsafeMatches = order?.unsafeAreaMatches;
    if (
      requiresUnsafeAreaAcknowledgment(unsafeMatches) &&
      !unsafeAreaAcknowledged
    ) {
      message.error(
        "Please acknowledge the unsafe-area risk before creating this booking"
      );
      return;
    }

    setLoading(true);
    try {
      const bookingData = {
        quoteId: quote.id,
        orderId: order?.id || orderId || null, // Allow null - backend will find/create order
        moveDate: values.moveDate.format("YYYY-MM-DD"),
        pickupTimeWindow: {
          type: values.pickupTimeWindow,
          // Set time windows based on type
          ...getTimeWindowForType(values.pickupTimeWindow),
        },
        driverId: values.driverId, // Changed from teamId to driverId
        notes: values.notes,
        adminId: userId,
        // BRIDGE: Manual vehicle selection
        manualVehicle: manualVehicle?.trim() || null,
        vehicleType: vehicleType || null,
      };

      const result = await createBooking(bookingData);

      if (result.success) {
        message.success("Booking created successfully!");
        if (onSuccess) {
          onSuccess(result.data);
        }
        handleClose();
      } else {
        message.error(result.error || "Failed to create booking");
      }
    } catch (error) {
      console.error("Error creating booking:", error);
      message.error("Failed to create booking. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Get time window for type
  const getTimeWindowForType = (type) => {
    const windows = {
      morning: { start: "08:00", end: "10:00" },
      midday: { start: "11:00", end: "13:00" },
      afternoon: { start: "14:00", end: "16:00" },
      flexible: { start: "08:00", end: "17:00" },
    };
    return windows[type] || windows.morning;
  };

  const handleClose = () => {
    form.resetFields();
    setAddressErrors({ pickup: null, delivery: null });
    setAddressValidated({ pickup: false, delivery: false });
    setCreatedDriver(null);
    setOrder(null);
    // BRIDGE: Reset manual vehicle fields
    setManualVehicle("");
    setVehicleType("medium");
    setUnsafeAreaAcknowledged(false);
    onClose();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
    }).format(amount);
  };

  if (!quote) {
    return null;
  }

  const bookingFooter = loadingOrder ? null : (
    <div style={{ textAlign: "right" }}>
      <Space>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          form="booking-confirmation-form"
          loading={loading}
          icon={<CheckCircleOutlined />}
          disabled={
            !addressValidated.pickup ||
            !addressValidated.delivery ||
            (requiresUnsafeAreaAcknowledgment(order?.unsafeAreaMatches) &&
              !unsafeAreaAcknowledged)
          }
        >
          Confirm & Create Booking
        </Button>
      </Space>
    </div>
  );

  return (
    <>
    <ResponsiveDrawer
      title={
        <Space>
          <CalendarOutlined />
          <span>Confirm Booking Details</span>
        </Space>
      }
      open={visible}
      onClose={handleClose}
      footer={bookingFooter}
      desktopWidth={720}
      destroyOnHidden
      maskClosable={!loading}
    >
      {loadingOrder ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Text>Loading order data...</Text>
        </div>
      ) : (
        <Form
          id="booking-confirmation-form"
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            pickupTimeWindow: "morning",
          }}
        >
          {/* Quote & Order Summary */}
          <Card size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Text type="secondary">Quote ID:</Text>
                <br />
                <Text strong>{quote.id}</Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">Order ID:</Text>
                <br />
                <Text strong>{order?.id || orderId}</Text>
              </Col>
              <Col span={12} style={{ marginTop: 8 }}>
                <Text type="secondary">Customer:</Text>
                <br />
                <Text strong>{quote.customerName || order?.customer}</Text>
              </Col>
              <Col span={12} style={{ marginTop: 8 }}>
                <Text type="secondary">Total Cost:</Text>
                <br />
                <Text strong style={{ fontSize: "16px", color: "#1890ff" }}>
                  {formatCurrency(
                    quote.totalCost || quote.estimatedCost || 0
                  )}
                </Text>
              </Col>
            </Row>
          </Card>

          <UnsafeAreaAlert
            matches={order?.unsafeAreaMatches}
            variant="antd"
          />
          {requiresUnsafeAreaAcknowledgment(order?.unsafeAreaMatches) && (
              <Checkbox
                checked={unsafeAreaAcknowledged}
                onChange={(e) => setUnsafeAreaAcknowledged(e.target.checked)}
                style={{ marginBottom: 16 }}
              >
                I acknowledge the unsafe-area risk (Tier 1–2) and have briefed
                ops / crew on the safety protocol
              </Checkbox>
            )}

          {/* Address Validation Section */}
          <Card
            size="small"
            style={{ marginBottom: 16 }}
            title={
              <Space>
                <HomeOutlined />
                <span>Address Validation</span>
              </Space>
            }
          >
            {/* Pickup Address */}
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <Text strong>Pickup Address:</Text>
                {!editingAddress.pickup ? (
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => handleEditAddress("pickup")}
                  >
                    Edit
                  </Button>
                ) : null}
              </div>

              {editingAddress.pickup ? (
                <div>
                  <TextArea
                    value={addressInputs.pickup}
                    onChange={(e) =>
                      setAddressInputs((prev) => ({
                        ...prev,
                        pickup: e.target.value,
                      }))
                    }
                    placeholder="Enter pickup address (e.g., 11 Conrad Dr, Blairgowrie, Randburg, Johannesburg, Gauteng)"
                    rows={2}
                    style={{ marginBottom: 8 }}
                  />
                  <Space>
                    <Button
                      type="primary"
                      size="small"
                      loading={savingAddress.pickup}
                      onClick={() => handleSaveAddress("pickup")}
                      disabled={loadingOrder || !order?.id}
                    >
                      Save
                    </Button>
                    <Button
                      size="small"
                      onClick={() => handleCancelEditAddress("pickup")}
                      disabled={savingAddress.pickup}
                    >
                      Cancel
                    </Button>
                  </Space>
                </div>
              ) : (
                <>
                  <Text code style={{ fontSize: "13px" }}>
                    {order?.pickup?.address || "N/A"}
                  </Text>
                  {addressValidated.pickup ? (
                    <Tag color="success" style={{ marginLeft: 8 }}>
                      <CheckCircleOutlined /> Valid
                    </Tag>
                  ) : addressErrors.pickup ? (
                    <Alert
                      message={addressErrors.pickup}
                      type="error"
                      showIcon
                      style={{ marginTop: 8 }}
                      icon={<WarningOutlined />}
                    />
                  ) : null}
                </>
              )}
            </div>

            {/* Delivery Address */}
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <Text strong>Delivery Address:</Text>
                {!editingAddress.delivery ? (
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => handleEditAddress("delivery")}
                  >
                    Edit
                  </Button>
                ) : null}
              </div>

              {editingAddress.delivery ? (
                <div>
                  <TextArea
                    value={addressInputs.delivery}
                    onChange={(e) =>
                      setAddressInputs((prev) => ({
                        ...prev,
                        delivery: e.target.value,
                      }))
                    }
                    placeholder="Enter delivery address (e.g., 11 Conrad Dr, Blairgowrie, Randburg, Johannesburg, Gauteng)"
                    rows={2}
                    style={{ marginBottom: 8 }}
                  />
                  <Space>
                    <Button
                      type="primary"
                      size="small"
                      loading={savingAddress.delivery}
                      onClick={() => handleSaveAddress("delivery")}
                      disabled={loadingOrder || !order?.id}
                    >
                      Save
                    </Button>
                    <Button
                      size="small"
                      onClick={() => handleCancelEditAddress("delivery")}
                      disabled={savingAddress.delivery}
                    >
                      Cancel
                    </Button>
                  </Space>
                </div>
              ) : (
                <>
                  <Text code style={{ fontSize: "13px" }}>
                    {order?.delivery?.address || "N/A"}
                  </Text>
                  {addressValidated.delivery ? (
                    <Tag color="success" style={{ marginLeft: 8 }}>
                      <CheckCircleOutlined /> Valid
                    </Tag>
                  ) : addressErrors.delivery ? (
                    <Alert
                      message={addressErrors.delivery}
                      type="error"
                      showIcon
                      style={{ marginTop: 8 }}
                      icon={<WarningOutlined />}
                    />
                  ) : null}
                </>
              )}
            </div>

            {(!addressValidated.pickup || !addressValidated.delivery) && (
              <Alert
                message="Address validation failed"
                description="Addresses must include: Street number, Street name, Suburb, Town, and Province. Click 'Edit' to update the addresses."
                type="warning"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}

            <div style={{ marginTop: 12, fontSize: "12px", color: "#666" }}>
              <Text type="secondary">
                Example format: "11 Conrad Dr, Blairgowrie, Randburg,
                Johannesburg, Gauteng"
              </Text>
            </div>
          </Card>

          {/* Booking Details Form */}
          <Divider orientation="left">Booking Details</Divider>

          <Form.Item
            name="moveDate"
            label="Move Date"
            rules={[{ required: true, message: "Move date is required" }]}
          >
            <DatePicker
              style={{ width: "100%" }}
              format="YYYY-MM-DD"
              disabledDate={(current) => {
                // Disable past dates
                return current && current < dayjs().startOf("day");
              }}
              onChange={handleDateChange}
              suffixIcon={<CalendarOutlined />}
            />
          </Form.Item>

          <Form.Item
            name="pickupTimeWindow"
            label="Pickup Time Window"
            rules={[{ required: true, message: "Time window is required" }]}
          >
            <Select>
              <Option value="morning">
                <ClockCircleOutlined /> Morning (08:00 - 10:00)
              </Option>
              <Option value="midday">
                <ClockCircleOutlined /> Midday (11:00 - 13:00)
              </Option>
              <Option value="afternoon">
                <ClockCircleOutlined /> Afternoon (14:00 - 16:00)
              </Option>
              <Option value="flexible">
                <ClockCircleOutlined /> Flexible (08:00 - 17:00)
              </Option>
            </Select>
          </Form.Item>

          <ProviderDriverAssignmentFields
            form={form}
            driverRequired
            allowCreateDriver
            resetKey={visible}
            reloadToken={driverReloadToken}
            createdDriver={createdDriver}
            onCreateDriver={(provider) => {
              if (provider && !isBubakiiProvider(provider)) {
                setPartners((prev) =>
                  prev.some((partner) => partner.id === provider.id)
                    ? prev
                    : [...prev, provider]
                );
              }
              setNewDriverData((prev) => ({
                ...prev,
                name: "",
                ownerType: isBubakiiProvider(provider)
                  ? "bubakii"
                  : "third_party",
                partnerId: isBubakiiProvider(provider)
                  ? ""
                  : provider?.id || "",
              }));
              setCreateDriverModalVisible(true);
            }}
          />

          {/* BRIDGE: Manual Vehicle Selection */}
          <Divider orientation="left">Vehicle Information (Bridge Solution)</Divider>
          
          <Form.Item
            name="vehicleType"
            label="Vehicle Type"
            help="Select vehicle type for the move"
          >
            <Select
              value={vehicleType}
              onChange={(value) => setVehicleType(value)}
              placeholder="Select vehicle type"
            >
              <Option value="small">Small</Option>
              <Option value="medium">Medium</Option>
              <Option value="large">Large</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="manualVehicle"
            label="Manual Vehicle Description (Optional)"
            help="Enter vehicle description if not in system (e.g., '4 ton', '8 ton with trailer'). Leave empty to use system vehicles."
          >
            <Input
              value={manualVehicle}
              onChange={(e) => setManualVehicle(e.target.value)}
              placeholder="e.g., 4 ton, 8 ton with trailer"
            />
          </Form.Item>

          {/* Special Notes */}
          <Divider orientation="left">Special Notes</Divider>

          <Form.Item name={["notes", "stairs"]} label="Stairs">
            <Input placeholder="e.g., 2 flights, no elevator" />
          </Form.Item>

          <Form.Item name={["notes", "accessRules"]} label="Access Rules">
            <TextArea
              rows={2}
              placeholder="e.g., Gate code, security requirements"
            />
          </Form.Item>

          <Form.Item name={["notes", "fragileItems"]} label="Fragile Items">
            <TextArea rows={2} placeholder="List any fragile items" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name={["notes", "gateCode"]} label="Gate Code">
                <Input placeholder="Gate/security code" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["notes", "estateName"]} label="Estate Name">
                <Input placeholder="Estate/Complex name" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name={["notes", "contactOnDay"]}
            label="Contact Number (Day of Move)"
          >
            <Input placeholder="Phone number for day-of contact" />
          </Form.Item>

          {/* Action Buttons moved to ResponsiveDrawer footer */}
        </Form>
      )}
    </ResponsiveDrawer>

      {/* Create Driver Drawer */}
      <ResponsiveDrawer
        title="Create New Driver"
        open={createDriverModalVisible}
        onClose={() => {
          if (!creatingDriver) {
            setCreateDriverModalVisible(false);
            setCreateDriverError(null);
          }
        }}
        footer={
          <div style={{ textAlign: "right" }}>
            <Space>
              <Button
                onClick={() => {
                  setCreateDriverModalVisible(false);
                  setCreateDriverError(null);
                }}
                disabled={creatingDriver}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                form="booking-create-driver-form"
                loading={creatingDriver}
              >
                Create Driver
              </Button>
            </Space>
          </div>
        }
        desktopWidth={560}
        maskClosable={!creatingDriver}
      >
        {createDriverError && (
          <Alert
            message="Error"
            description={createDriverError}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Form
          id="booking-create-driver-form"
          form={createDriverForm}
          layout="vertical"
          onFinish={handleCreateDriver}
        >
          <Form.Item label="Name" required>
            <Input
              value={newDriverData.name}
              onChange={(e) =>
                setNewDriverData((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="Driver name"
              autoFocus
            />
          </Form.Item>

          <Form.Item label="Phone" required>
            <Input
              value={newDriverData.phone}
              onChange={(e) =>
                setNewDriverData((prev) => ({ ...prev, phone: e.target.value }))
              }
              placeholder="Phone number"
            />
          </Form.Item>

          <Form.Item label="Email">
            <Input
              type="email"
              value={newDriverData.email}
              onChange={(e) =>
                setNewDriverData((prev) => ({ ...prev, email: e.target.value }))
              }
              placeholder="Email address"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="License Number" required>
                <Input
                  value={newDriverData.licenseNumber}
                  onChange={(e) =>
                    setNewDriverData((prev) => ({
                      ...prev,
                      licenseNumber: e.target.value,
                    }))
                  }
                  placeholder="License number"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="License Expiry" required>
                <DatePicker
                  style={{ width: "100%" }}
                  value={
                    newDriverData.licenseExpiry
                      ? dayjs(newDriverData.licenseExpiry)
                      : null
                  }
                  onChange={(date) =>
                    setNewDriverData((prev) => ({
                      ...prev,
                      licenseExpiry: date ? date.toDate() : null,
                    }))
                  }
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Owner Type" required>
            <Select
              value={newDriverData.ownerType}
              onChange={(value) => {
                setNewDriverData((prev) => ({
                  ...prev,
                  ownerType: value,
                  partnerId: value !== "third_party" ? "" : prev.partnerId,
                }));
              }}
            >
              <Option value="bubakii">Bubakii</Option>
              <Option value="third_party">3rd Party</Option>
            </Select>
          </Form.Item>

          {newDriverData.ownerType === "third_party" && (
            <Form.Item
              label="Partner"
              required
              validateStatus={
                createDriverError && !newDriverData.partnerId ? "error" : ""
              }
              help={
                createDriverError && !newDriverData.partnerId
                  ? "Partner is required for 3rd party drivers"
                  : ""
              }
            >
              <PartnerSearchAutocomplete
                value={
                  newDriverData.partnerId
                    ? typeof newDriverData.partnerId === "string"
                      ? partners.find((p) => p.id === newDriverData.partnerId) ||
                        null
                      : newDriverData.partnerId
                    : null
                }
                onChange={(partner) => {
                  if (partner) {
                    setNewDriverData((prev) => ({
                      ...prev,
                      partnerId: partner.id,
                    }));
                    // Update partners list if new partner was created
                    if (!partners.find((p) => p.id === partner.id)) {
                      setPartners((prev) => [...prev, partner]);
                    }
                  } else {
                    setNewDriverData((prev) => ({ ...prev, partnerId: "" }));
                  }
                }}
                label="Partner"
                required
                error={
                  createDriverError && !newDriverData.partnerId
                    ? "Partner is required"
                    : null
                }
              />
            </Form.Item>
          )}
        </Form>
      </ResponsiveDrawer>
    </>
  );
};

export default BookingConfirmationModal;

